import * as assert from "typed-assert";
import {
  Versioned,
  PaymentFrequency,
  PaymentSourceKind,
} from "@evergiving/common/dist/es2015/pledge";
import { FormConfig } from "@evergiving/common/dist/es2015/form";
import { formatAmount } from "@/lib/misc/parseAmount";
import { formatISO } from "date-fns";
import { getStartDates } from "@/lib/misc/startDateUtil";

import {
  RootState,
  RootAction,
  SetFormValueAction,
  SET_PRESET_AMOUNT,
  SET_CUSTOM_CURRENCY,
  SET_PAYMENT_FREQUENCY,
  SET_PAYMENT_SOURCE_KIND,
  SET_PAYMENT_DAY,
  SET_POSTAL_ADDRESS,
  SET_COUNTRY_CODE,
  SET_BELFIORE_CODE,
  SET_START_DATE,
  SET_FORM_VALUE,
  SET_BOOLEAN_FORM_VALUE,
  SET_PARTIAL_STATE,
  RESET_FORM_ERRORS,
  SET_FORM_ERRORS,
  SET_FORM_SOURCE_REF,
  SET_EMAIL_ADDRESS,
  SET_IBAN,
} from "./types";

function upversion<T>(value: T, oldVersion: Versioned<T>) {
  return {
    value,
    version: oldVersion.version + 1,
  };
}

function deriveCardHolderName(state: RootState) {
  if (state.firstName.value && state.lastName.value) {
    return [state.firstName.value, state.lastName.value].join(" ");
  }
  return undefined;
}

function maybeUpdatedStartDate(
  current: string | undefined,
  startDates: Date[]
): string | undefined {
  const startDateStrings = startDates.map((date) =>
    formatISO(date, { representation: "date" })
  );
  if (current && startDateStrings.includes(current)) {
    return current;
  }
  return startDateStrings[0];
}

const getNewStateForSetFormValue = (
  state: RootState,
  payload: SetFormValueAction["payload"]
) => ({
  ...state,
  [payload.attribute]: upversion(payload.value, state[payload.attribute]),
});

export function rootReducer(
  state: RootState | undefined,
  action: RootAction
): RootState {
  assert.isNotUndefined(state);
  switch (action.type) {
    case SET_PRESET_AMOUNT:
      return {
        ...state,
        presetAmount: action.payload.amount,
        customAmountString: {
          version: state.customAmountString?.version || 1,
          value: action.payload.amount
            ? formatAmount(action.payload.locale, action.payload.amount.value)
            : undefined,
        },
        errors: { ...state.errors, customAmountString: false },
      };
    case SET_CUSTOM_CURRENCY:
      return {
        ...state,
        presetAmount: undefined,
        customCurrency: action.payload,
      };
    case SET_PAYMENT_FREQUENCY: {
      const { paymentSourceKind, startDate } = state;
      const startDates = getStartDates(action.payload, paymentSourceKind);
      return {
        ...state,
        paymentFrequency: action.payload,
        startDate: maybeUpdatedStartDate(startDate, startDates),
      };
    }
    case SET_PAYMENT_SOURCE_KIND: {
      const { paymentFrequency, startDate } = state;
      const startDates = getStartDates(paymentFrequency, action.payload);
      return {
        ...state,
        paymentSourceKind: action.payload,
        startDate: maybeUpdatedStartDate(startDate, startDates),
      };
    }
    case SET_PAYMENT_DAY:
      return {
        ...state,
        paymentDay: action.payload,
      };
    case SET_POSTAL_ADDRESS:
      return {
        ...state,
        postalAddress: action.payload,
      };
    case SET_COUNTRY_CODE:
      return {
        ...state,
        countryCode: upversion(action.payload, state.countryCode),
      };
    case SET_BELFIORE_CODE:
      return {
        ...state,
        belfioreCode: upversion(action.payload, state.belfioreCode),
      };
    case SET_START_DATE:
      return {
        ...state,
        startDate: action.payload,
      };
    case SET_FORM_VALUE:
      return {
        ...getNewStateForSetFormValue(state, action.payload),
        cardHolder:
          state.cardHolder.version === 0 &&
          state.cardHolder.value !==
            deriveCardHolderName(
              getNewStateForSetFormValue(state, action.payload)
            )
            ? {
                value: deriveCardHolderName(
                  getNewStateForSetFormValue(state, action.payload)
                ),
                version: 0,
              }
            : state.cardHolder,
        errors: {
          ...state.errors,
          [action.payload.attribute]: action.payload.anyErrors,
        },
      };
    case RESET_FORM_ERRORS:
      return {
        ...state,
        errors: {
          ...state.errors,
          [action.payload]: false,
        },
      };
    case SET_FORM_ERRORS:
      return {
        ...state,
        errors: action.payload,
      };
    case SET_BOOLEAN_FORM_VALUE:
      return {
        ...state,
        [action.payload.attribute]: action.payload.value,
        errors: {
          ...state.errors,
          [action.payload.attribute]: action.payload.anyErrors,
        },
      };
    case SET_PARTIAL_STATE:
      return {
        ...state,
        ...action.payload,
      };
    case SET_FORM_SOURCE_REF:
      return {
        ...state,
        formSourceRef: action.payload,
      };
    case SET_EMAIL_ADDRESS:
      return {
        ...state,
        emailAddress: {
          value: action.payload.emailAddress,
          version:
            (state.emailAddress.version || 0) +
            (action.payload.emailAddress === state.emailAddress.value ? 0 : 1),
        },
        errors: {
          ...state.errors,
          emailAddress: action.payload.anyErrors,
        },
        emailAppraisal: action.payload.appraisal,
      };
    case SET_IBAN:
      return {
        ...state,
        directDebitIBAN: {
          value: action.payload.iban,
          version:
            (state.directDebitIBAN.version || 0) +
            (action.payload.iban === state.directDebitIBAN.value ? 0 : 1),
        },
        errors: {
          ...state.errors,
          directDebitIBAN: action.payload.anyErrors,
        },
        ibanAppraisal: action.payload.appraisal,
      };
    default:
      return state;
  }
}

export function getPaymentFrequencyDefaults(
  formConfig: FormConfig,
  paymentFrequency: PaymentFrequency | undefined,
  paymentSourceKind: PaymentSourceKind | undefined
): Pick<
  RootState,
  "presetAmount" | "customAmountString" | "customCurrency" | "startDate"
> {
  const defaultFrequencyConfig =
    paymentFrequency && "payment" in formConfig
      ? formConfig.payment.frequencies[paymentFrequency]
      : undefined;
  const defaultAmount = defaultFrequencyConfig?.defaultAmount;
  const defaultPresetAmount =
    defaultFrequencyConfig &&
    defaultAmount &&
    defaultFrequencyConfig.presetAmounts.find(
      (presetAmount) =>
        presetAmount.value === defaultAmount.value &&
        presetAmount.currency === defaultAmount.currency
    );

  const startDate = getStartDates(paymentFrequency, paymentSourceKind)[0];

  return {
    presetAmount: defaultPresetAmount,
    customAmountString: {
      value:
        defaultPresetAmount === undefined && defaultAmount !== undefined
          ? formatAmount(formConfig.locale, defaultAmount.value)
          : undefined,
      version: 0,
    },
    customCurrency:
      defaultPresetAmount === undefined && defaultAmount !== undefined
        ? defaultAmount.currency
        : defaultFrequencyConfig?.customAmount?.allowedCurrencies[0],
    startDate: startDate
      ? formatISO(startDate, { representation: "date" })
      : undefined,
  };
}
